import { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Avatar, Dropdown, Button, theme } from 'antd';
import {
  DashboardOutlined,
  LineChartOutlined,
  SmileOutlined,
  CheckSquareOutlined,
  FileTextOutlined,
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ClockCircleOutlined,
  CustomerServiceOutlined,
  AppstoreOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@tradermind/shared/stores';
import { ROUTES } from '@tradermind/shared/constants';

const { Header, Sider, Content } = Layout;

export default function MainLayout() {
  const { t } = useTranslation('common');

  const menuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: t('nav.dashboard'),
    },
    {
      key: '/emotions',
      icon: <SmileOutlined />,
      label: t('nav.emotions'),
    },
    {
      key: '/checklist/pre-trade',
      icon: <CheckSquareOutlined />,
      label: t('nav.checklist'),
    },
    {
      key: '/strategies',
      icon: <AppstoreOutlined />,
      label: t('nav.strategies'),
    },
    {
      key: '/trades',
      icon: <LineChartOutlined />,
      label: t('nav.trades'),
    },
    {
      key: '/profit-calendar',
      icon: <CalendarOutlined />,
      label: t('nav.profit_calendar'),
    },
    {
      key: '/cooldown',
      icon: <ClockCircleOutlined />,
      label: t('nav.cooldown'),
    },
    {
      key: '/reports/weekly',
      icon: <FileTextOutlined />,
      label: t('nav.reports'),
    },
    {
      key: '/profile',
      icon: <UserOutlined />,
      label: t('nav.profile'),
    },
    {
      key: '/support',
      icon: <CustomerServiceOutlined />,
      label: t('nav.support'),
    },
    {
      key: '/settings',
      icon: <SettingOutlined />,
      label: t('nav.settings'),
    },
  ];
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  const handleLogout = () => {
    logout();
    navigate(ROUTES.AUTH.LOGIN);
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: t('nav.profile'),
      onClick: () => navigate('/profile'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: t('nav.settings'),
      onClick: () => navigate('/settings'),
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('nav.logout'),
      onClick: handleLogout,
    },
  ];

  const getSelectedKey = () => {
    const path = location.pathname;
    if (path.startsWith('/profit-calendar')) return '/profit-calendar';
    if (path.startsWith('/trades')) return '/trades';
    if (path.startsWith('/emotions')) return '/emotions';
    if (path.startsWith('/checklist')) return '/checklist/pre-trade';
    if (path.startsWith('/strategies')) return '/strategies';
    if (path.startsWith('/cooldown')) return '/cooldown';
    if (path.startsWith('/reports')) return '/reports/weekly';
    if (path.startsWith('/support')) return '/support';
    return path;
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider trigger={null} collapsible collapsed={collapsed} theme="dark">
        <div
          style={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: collapsed ? 16 : 20,
            fontWeight: 600,
          }}
        >
          {collapsed ? 'TM' : 'TradingMind'}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[getSelectedKey()]}
          items={menuItems}
          onClick={handleMenuClick}
        />
      </Sider>
      <Layout>
        <Header
          style={{
            padding: '0 24px',
            background: colorBgContainer,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{ fontSize: 16, width: 64, height: 64 }}
          />
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Avatar src={user?.avatar_url} icon={<UserOutlined />} />
              <span>{user?.nickname || user?.email}</span>
            </div>
          </Dropdown>
        </Header>
        <Content
          style={{
            margin: 24,
            padding: 24,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
            minHeight: 280,
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
